// BFF protocol endpoints do not expose OAuth tokens to the SPA.
export type SsoConfiguration = {
  enabled: boolean
  profile?: string | null
  corporateRegistrationEnabled?: boolean
  inviteContinuationEnabled?: boolean
}

let configuration: Promise<SsoConfiguration> | undefined
let csrf: { headerName: string; requestToken: string } | undefined

export function getSsoConfiguration() {
  configuration ??= fetch('/bff/config', { credentials: 'same-origin', cache: 'no-store' })
    .then(async (response) => {
      if (!response.ok) throw new Error('Não foi possível consultar o login corporativo.')
      const result = (await response.json()) as SsoConfiguration
      if (typeof result.enabled !== 'boolean') throw new Error('Configuração de login inválida.')
      return result
    })
    .catch((error) => {
      configuration = undefined
      throw error
    })

  return configuration
}

export async function getSsoCsrf() {
  if (!csrf) {
    const response = await fetch('/bff/antiforgery', {
      credentials: 'same-origin',
      cache: 'no-store',
    })
    if (!response.ok) throw new Error('Não foi possível proteger a sessão.')
    const value = (await response.json()) as { headerName?: string; requestToken: string }
    if (!value.requestToken) throw new Error('Proteção de sessão inválida.')
    csrf = { headerName: value.headerName ?? 'X-CSRF-Token', requestToken: value.requestToken }
  }
  return csrf
}

/** Páginas públicas do fluxo SSO: o erro de restauração de sessão não pode substituí-las. */
export function isPublicSsoPath(pathname: string) {
  return (
    pathname === '/login' ||
    pathname === '/acesso/convite' ||
    pathname.startsWith('/acesso/convite/')
  )
}

export function clearSsoCsrf() {
  csrf = undefined
}

export async function logoutSso() {
  const token = await getSsoCsrf()
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = '/bff/logout?returnUrl=/login'
  // Foundation uses an antiforgery header for API commands and the native form field for navigation.
  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = '__RequestVerificationToken'
  input.value = token.requestToken
  form.append(input)
  document.body.append(form)
  form.submit()
}

export type SsoSession = { authenticated: boolean; name?: string }

/** Estado da sessão atual no BFF (/bff/session); nunca expõe tokens. */
export async function getSsoSession(): Promise<SsoSession> {
  const response = await fetch('/bff/session', { credentials: 'same-origin', cache: 'no-store' })
  if (response.status === 401) return { authenticated: false }
  if (!response.ok) throw new Error('Não foi possível consultar a sessão atual.')
  const value = (await response.json()) as { autenticado?: boolean; nome?: string }
  return { authenticated: value.autenticado === true, name: value.nome }
}

export type InviteContinuationMode = 'new' | 'switch'

/**
 * Inicia a continuação do convite. É um POST de navegação (como o logout): o BFF cria o contexto
 * no servidor e responde com o redirecionamento ao IdP. Nada do contexto passa pela SPA.
 */
export async function startInviteContinuation(mode: InviteContinuationMode, expectedEmail: string) {
  // Sem o e-mail do convite não há como confirmar a conta esperada; o servidor também recusa.
  const email = expectedEmail.trim()
  if (!email) throw new Error('Informe o e-mail do convite.')

  const token = await getSsoCsrf()
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = '/bff/convite/continuar'
  const fields: Record<string, string> = {
    __RequestVerificationToken: token.requestToken,
    mode,
    expectedEmail: email,
  }
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.append(input)
  }
  document.body.append(form)
  form.submit()
}

export type InviteResultCode =
  | 'concluido'
  | 'ja-ativo'
  | 'divergente'
  | 'expirado'
  | 'nao-concluido'

const INVITE_RESULTS: readonly string[] = [
  'concluido',
  'ja-ativo',
  'divergente',
  'expirado',
  'nao-concluido',
]

/**
 * Resultado da continuação do convite, confirmado pelo servidor para esta sessão. É de leitura única: o BFF consome a
 * evidência. Nulo quando não há evidência válida (abertura direta, recarga, sessão ou conta diferente, expirada).
 * Nada na URL participa da decisão.
 */
export async function getInviteResult(): Promise<InviteResultCode | null> {
  const response = await fetch('/bff/convite/resultado', {
    credentials: 'same-origin',
    cache: 'no-store',
  })
  if (!response.ok) throw new Error('Não foi possível consultar o resultado do convite.')
  const value = (await response.json()) as { resultado?: string | null }
  return typeof value.resultado === 'string' && INVITE_RESULTS.includes(value.resultado)
    ? (value.resultado as InviteResultCode)
    : null
}
